﻿import { IInputScope, KbInput } from "@app/components/kb-input/kb-input.component";
import { NgComponent } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { Dirs } from "@app/helpers/dirs";
import { SearchUtils, Utils } from "@tools";
import * as angular from "angular";

export interface ISelectScope extends IInputScope {
    // external members
    source: any;
    /** custom data that can be passed in and accessed by the transcluded content for the select items */
    data: any;
    valueField: string;
    labelField: string;
    defaultLabel: string;
    headerTemplate: string;
    mobileDropdown: boolean;
    /** if true, then a search box will be available in the dropdown */
    filter: boolean;
    itemStyle: any;
    optionLayout: string;
    /** for convenience when the consumer wants to have a custom headerTemplate, 
     * they can bind to the selected data item 
     */
    item: any;
    /** by default, kbSelect only watches for top level changes to the source array (like whether items are added or removed), but
     * doesn't watch the properties of items.  To watch the properties also, set deepWatch to true.
     */
    deepWatch: boolean;
        

    // internal directive members
    _activeIndex: number;
    _selectedIndex: number;
    _select: (item: any) => void;
    _click: (e: JQueryEventObject) => void;
    _dropdownOpen: boolean;
    _selectedLabel: string;
    _queryChange: (e: ng.IAngularEvent) => void;
    _queryKeydown: (e: JQueryEventObject) => void;
    _id: string;
    _binding: { query: string };

    _filteredSource: any[];
    _isActive: (index) => boolean;
    _itemMousedown: (e: JQueryEventObject, item: any) => void;
}

export interface ISelectController {
    scope: ISelectScope;
}

@NgComponent({
    token: Token.KbSelect,
    dependencies: [
        Token.$Timeout,
        Token.$Window,
    ]
})
export class KbSelect extends KbInput {
    constructor(
        $timeout: ng.ITimeoutService,
        $window: ng.IWindowService
    ) {
        super($timeout);

        this.template = Dirs.component("kb-select");
        this.scope = Utils.extend(this.scope, {
            source: "=",
            data: "=?",
            valueField: "@",
            labelField: "@",
            defaultLabel: "@",
            headerTemplate: "@",
            mobileDropdown: "=?",
            filter: "=",
            itemStyle: "=?",
            deepWatch: "=?",
            optionLayout: "=?"
        });
        this.transclude = true;

        this.compile = (element, atts: any) => {
            if (atts.mobileDropdown == null) atts.mobileDropdown = "true";
            return this.link;
        };

        this.controller = [
            "$scope",
            "$element",
            "$attrs",
            "$transclude",
            "$templateCache",
            Token.$Compile.key,
            function(
                $scope: ISelectScope,
                $element: ng.IAugmentedJQuery,
                $attrs: ng.IAttributes,
                $transclude: ng.ITranscludeFunction,
                $templateCache: ng.ITemplateCacheService,
                $compile: ng.ICompileService) {
                // set the scope on the controller so children can access it
                this.scope = $scope;
                                       
                // if a header template was passed in then get it from the template cache
                if ($scope.headerTemplate) {
                    let $template = angular.element($templateCache.get($scope.headerTemplate));
                    // compile the template
                    let compiled = $compile($template, $transclude);
                    // append it to the label
                    $element.find(".kb-select__header").html("").append($template);
                    // now link it to the parent scope(we don't want this scope because it's an isolate scope)
                    compiled($scope);
                }
            }];

        this.link = (scope: ISelectScope, element: JQuery, atts, controllers: any[]) => {
            // we provide out own implementation of isEmpty to kbInput since a value because null is an allowed value that could have a label
            // this is for material design so we know when to float the label
            let isEmpty = (val: any = null) => {
                return scope._selectedLabel == null || scope._selectedLabel.length == 0;
            };
            this.kbInputLinkFn(scope, element, atts, controllers, true, isEmpty);
            let ngModelCtrl = controllers[0];

            scope._id = Utils.shortId();
            scope._binding = { query: "" };
            let input = element.find("input");
            // the source as an array (it might have been passed in as a function so we normalize it)
            let arraySource: any[];
            //readyForValue tracks wether a value has been chosen for the select list or not 
            var readyForValue = false;

            let getItemValue = (item: any) => {
                if (item === null || item === undefined) return null;
                return scope.valueField ? item[scope.valueField] : item;
            };
            let getItemLabel = (item: any): string => {
                if (scope.labelField && item[scope.labelField] && item[scope.labelField]?.toString()) {
                    return item[scope.labelField]?.toString();
                } else if (scope.valueField) {
                    return item[scope.valueField];
                } else {
                    return item;
                }
            };

            let updateSelectedLabel = () => {
                scope._selectedLabel = scope.item ? getItemLabel(scope.item) : scope.defaultLabel;
            };

            let isEnabled = () => {
                return !scope.ngDisabled;
            };

            let updateSelected = () => {
                if (arraySource) {
                    for (let i = 0; i < arraySource.length; i++) {
                        let item = arraySource[i];
                        if (getItemValue(item) == ngModelCtrl.$viewValue) {
                            scope.item = item;
                            scope._selectedIndex = i;
                            scope._activeIndex = i;
                            updateSelectedLabel();
                            scope._setHasValue(!isEmpty());
                            return;
                        }
                    }
                }
                // if we get here, then it wasn't found.
                scope.item = null;
                updateSelectedLabel();
                scope._setHasValue(!isEmpty());
            };

            let showDropdown = () => {
                scope._dropdownOpen = true;
                readyForValue = true;
                
                // on mobile the filter input will kick off the keyboard, which we don't necessarily want.  
                // The problem is worse on IOS because of focus restrictions
                if ($window.innerWidth >= Utils.MOBILE_WIDTH && !Utils.isIos()) {
                    $timeout(() => input.trigger('focus'));

                    if (scope.source.length > 10) {
                        let searchBox = document.getElementById(scope._id + "-input");
                        $timeout(() => searchBox.focus());
                    }
                }
            };

            let hideDropdown = () => {
                scope._dropdownOpen = false;
                scope._binding.query = null;
                updateFilteredSource();
            };
            let toggleDropdown = () => {
                scope._dropdownOpen ? hideDropdown() : showDropdown();
            };

            // options might be added after the ngModel is set by the consumer, so
            // we need to refind the selected option 
            let sourceChange = () => {
                if (angular.isFunction(scope.source)) {
                    arraySource = scope.source();
                } else {
                    arraySource = scope.source;
                }
                updateSelected();
                updateFilteredSource();
            }

            if (scope.deepWatch) {
                scope.$watch("source", () => sourceChange(), true);
            } else {
                scope.$watchCollection("source", () => sourceChange());
            }

            ngModelCtrl.$render = () => {
                updateSelected();
            };

            scope._itemMousedown = (e, item) => {
                e.preventDefault(); // so we don't lose focus
                scope._select(item);
            };

            scope._select = (item: any) => {
                if (readyForValue) {
                    if (isEnabled()) {
                        readyForValue = false;
                        hideDropdown();
                        ngModelCtrl.$setViewValue(getItemValue(item));
                        updateSelected();
                    }
                }
            };

            scope._click = e => {
                if (isEnabled()) {
                    toggleDropdown();
                }
            };

            scope._isActive = index => {
                return index === scope._activeIndex;
            };

            let updateFilteredSource = () => {
                if (!scope._binding.query) {
                    scope._filteredSource = arraySource;
                } else {
                    scope._filteredSource = SearchUtils.search(
                        scope._binding.query,
                        arraySource,
                        o => getItemLabel(o)?.toString(),
                        10000
                    );
                    scope._activeIndex = 0;
                }
            };

            scope._queryChange = e => {
                updateFilteredSource();
            };

            scope._queryKeydown = e => {
                if (e.key == "ArrowUp") {
                    moveUp();
                    e.stopPropagation();
                }
                if (e.key == "ArrowDown") {
                    moveDown();
                    e.stopPropagation();
                }
                if (e.key === "Enter") {
                    enter();
                    e.stopPropagation();
                }
                if (e.key === "Tab") {
                    tab();
                    if (scope._dropdownOpen) {
                        e.stopPropagation();
                        e.preventDefault();
                        
                    }
                }
            };

            let moveUp = () => {
                if (scope._dropdownOpen) {
                    // if we are already at index zero, then close the dropdown
                    let newIndex = scope._activeIndex - 1;
                    if (newIndex >= 0) scope._activeIndex = newIndex;
                }
            };

            let moveDown = () => {
                showDropdown();
                // if we are already at index zero, then close the dropdown
                let newIndex = scope._activeIndex + 1;
                if (newIndex < scope._filteredSource.length) scope._activeIndex = newIndex;
            };
                
            let enter = () => {
                if (scope._dropdownOpen) {
                    if (scope._filteredSource.length && scope._activeIndex > -1) {
                        scope._select(scope._filteredSource[scope._activeIndex]);
                    }
                    element.trigger("focus");
                } else {
                    showDropdown();
                }
            };

            let tab = () => {
                if (scope._dropdownOpen) {
                    element.trigger('focus');
                    if (scope._filteredSource.length && scope._activeIndex > -1) {
                        scope._select(scope._filteredSource[scope._activeIndex]);
                    }
                }
            };

            let keySearch = (key) => {
                key = key.toLowerCase();
                let option = scope._activeIndex;
                let labelOrValue = scope._filteredSource[option].label?.toString() ?? scope._filteredSource[option].value;
                if (labelOrValue && !labelOrValue.toLowerCase().startsWith(key)) {

                    option = scope._filteredSource.findIndex(a => {
                        let b = a.label?.toString() || a.value;
                        return b.toLowerCase().startsWith(key);
                    });
                } else {
                    if ((option + 1) < scope._filteredSource.length) {
                        let optionPlusOne = scope._filteredSource[(option + 1)].label?.toString() ?? scope._filteredSource[(option + 1)].value;
                        if (optionPlusOne && optionPlusOne.toLowerCase().startsWith(key)) {
                            option++;
                        } else {
                            option = scope._filteredSource.findIndex(a => {
                                let b = a.label?.toString() ?? a.value;
                                return b.toLowerCase().startsWith(key);
                            });
                        }
                        
                    } else {
                        option = scope._filteredSource.findIndex(a => {
                            let b = a.label?.toString() ?? a.value;
                            return b?.toLowerCase().startsWith(key);
                        });
                    }
                }
                if (option > -1) {
                    showDropdown();
                    scope._activeIndex = option;
                }
                
            };

            // bind keyboard events: arrows up(38) / down(40), enter(13) and tab(9), esc(27)
            element.on("keydown", (e: JQuery.Event) => {
                switch (e.key) {
                    case 'ArrowUp':
                        e.preventDefault();
                        scope.$apply(() => {
                            moveUp();
                        });
                        break;
                    case 'ArrowDown':
                        e.preventDefault();
                        scope.$apply(() => {
                            moveDown();
                        });
                        break;
                    case 'Enter':
                        scope.$apply(() => {
                            e.preventDefault();
                            enter();
                        });
                        break;
                    case 'Tab':
                        scope.$apply(() => {
                            if (scope._dropdownOpen) {
                                e.stopPropagation();
                                e.preventDefault();
                            }
                            tab();
                        });
                        break;
                    case 'Escape':
                        e.preventDefault();
                        scope.$apply(() => {
                            hideDropdown();
                            e.stopPropagation();
                        });
                        element.trigger('blur');
                        break;
                    default:
                        if (e.key.match(/^[a-zA-Z,0-9]+$/) && e.key.length == 1) {
                            e.stopPropagation();
                            e.preventDefault();
                            scope.$apply(() => {
                                keySearch(e.key);
                            });
                        }
                        break;
                }
            });

            scope.$on("$destroy", () => {
                element.off();
            });
        };
    }
}
